import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Backdrop, Box } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { getEnvVariable } from '../../../envs';
import { useLoading } from './LoadingContext';
const chasePulse = keyframes `
  0%, 100% {
    transform: scale(0.38);
    opacity: 0.2;
    box-shadow: 0 0 0 0 currentColor;
  }
  40% {
    transform: scale(1.22);
    opacity: 1;
    box-shadow: 0 0 16px 1px currentColor;
  }
  55% {
    transform: scale(0.95);
    opacity: 0.85;
  }
`;
const ringDrift = keyframes `
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const ringCounter = keyframes `
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
`;
const meshBreath = keyframes `
  0%, 100% { opacity: 0.35; transform: scale(1) translate(0, 0); }
  50% { opacity: 0.55; transform: scale(1.08) translate(2%, -2%); }
`;
function DotRing({ count, radiusPx, dotSize, periodSec, colorForIndex, rotationMs, reverse, solidPulseColor, }) {
    const step = 360 / count;
    return (_jsx(Box, { sx: {
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: 0,
            height: 0,
            animation: `${reverse ? ringCounter : ringDrift} ${rotationMs}ms linear infinite`,
        }, children: Array.from({ length: count }, (_, i) => {
            const fill = solidPulseColor ? alpha(solidPulseColor, 0.62) : colorForIndex(i);
            const pulse = solidPulseColor ?? colorForIndex(i);
            return (_jsx(Box, { sx: {
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 0,
                    height: 0,
                    transform: `rotate(${i * step}deg)`,
                }, children: _jsx(Box, { sx: {
                        position: 'absolute',
                        left: -dotSize / 2,
                        top: -radiusPx,
                        width: dotSize,
                        height: dotSize,
                        borderRadius: '50%',
                        bgcolor: fill,
                        color: pulse,
                        animation: `${chasePulse} ${periodSec}s cubic-bezier(0.45, 0.02, 0.35, 1) infinite`,
                        animationDelay: `${(i / count) * periodSec}s`,
                        willChange: 'transform, opacity',
                    } }) }, i));
        }) }));
}
/**
 * Full-page (or embedded) load state: dual counter-rotating phased dot rings + mesh backdrop.
 */
export const PageLoadScreen = ({ variant = 'fullscreen', transparent = false, }) => {
    const theme = useTheme();
    const full = variant === 'fullscreen';
    const outerR = full ? 44 : 29;
    const innerR = full ? 24 : 15;
    const outerDot = full ? 8 : 6;
    const innerDot = full ? 6 : 4;
    const glyph = full ? 112 : 74;
    // Resolve dot colors directly from the env.json `palette` block at render time so the
    // loader always matches the *currently served* env.json, independent of when the MUI
    // theme captured the palette at module load. Falls back to the theme when env omits it.
    const envPalette = getEnvVariable('palette') || {};
    const primaryMain = envPalette?.primary?.main || theme.palette.primary.main;
    const primaryLight = envPalette?.primary?.light || theme.palette.primary.light;
    const secondaryMain = envPalette?.secondary?.main || theme.palette.secondary.main;
    const secondaryLight = envPalette?.secondary?.light || theme.palette.secondary.light;
    return (_jsxs(Box, { role: "status", "aria-live": "polite", "aria-busy": "true", sx: {
            position: 'relative',
            minHeight: full ? '100vh' : 140,
            ...(full
                ? {
                    '@supports (height: 100dvh)': {
                        minHeight: '100dvh',
                    },
                }
                : {}),
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            py: full ? 0 : 3,
            px: 2,
            ...(transparent
                ? {
                    bgcolor: 'transparent',
                    backgroundImage: 'none',
                }
                : {
                    bgcolor: alpha(theme.palette.primary.dark, 0.04),
                    backgroundImage: `
                radial-gradient(ellipse 85% 55% at 50% 18%, ${alpha(theme.palette.primary.main, 0.14)} 0%, transparent 52%),
                radial-gradient(ellipse 60% 45% at 80% 88%, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 50%),
                linear-gradient(165deg, ${alpha(theme.palette.background.default, 1)} 0%, ${alpha(theme.palette.grey[100], 0.96)} 100%)
              `,
                }),
        }, children: [_jsx(Box, { "aria-hidden": true, sx: {
                    position: 'absolute',
                    width: 'min(520px, 90vw)',
                    height: 'min(520px, 90vw)',
                    borderRadius: '50%',
                    left: '50%',
                    top: '42%',
                    transform: 'translate(-50%, -50%)',
                    background: `radial-gradient(circle at 35% 40%, ${alpha(primaryLight, 0.2)} 0%, transparent 45%),
            radial-gradient(circle at 65% 62%, ${alpha(secondaryMain, 0.14)} 0%, transparent 42%)`,
                    filter: 'blur(40px)',
                    animation: `${meshBreath} 7s ease-in-out infinite`,
                    pointerEvents: 'none',
                } }), _jsxs(Box, { sx: {
                    position: 'relative',
                    zIndex: 1,
                    width: glyph,
                    height: glyph,
                    mb: full ? 3 : 2,
                }, children: [_jsx(DotRing, { count: 12, radiusPx: outerR, dotSize: outerDot, periodSec: 1.45, rotationMs: 28000, colorForIndex: (i) => (i % 2 === 0 ? primaryMain : secondaryMain) }), _jsx(DotRing, { count: 6, radiusPx: innerR, dotSize: innerDot, periodSec: 1.15, rotationMs: 20000, reverse: true, colorForIndex: () => primaryMain, solidPulseColor: primaryMain }), _jsx(Box, { "aria-hidden": true, sx: {
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: alpha(secondaryMain, 0.35),
                            boxShadow: `0 0 24px 6px ${alpha(secondaryLight, 0.35)}`,
                            animation: `${chasePulse} 2.2s ease-in-out infinite`,
                            animationDelay: '0.35s',
                        } })] })] }));
};
const Loader = () => {
    const { isLoading, isBindingLoading } = useLoading();
    const isActive = isLoading || isBindingLoading;
    return (_jsx(Backdrop, { sx: {
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: 'transparent',
            backdropFilter: 'blur(2px)',
            transition: 'backdrop-filter 0.3s ease-in-out',
            backgroundColor: 'transparent',
        }, open: isActive, children: _jsx(PageLoadScreen, { variant: "fullscreen", transparent: true }) }));
};
export default Loader;
